/**
 * Imperative toast API.
 *
 * Usage (anywhere in the app, no import of context needed):
 *
 *   import { toast } from '@/lib/toast';
 *
 *   toast.success({ message: 'Profile saved!' });
 *   toast.error({ message: 'Upload failed', title: 'Error' });
 *   toast.info({ message: 'New version available', duration: 6000 });
 *
 * The `ToastProvider` (from `@/context/ToastContext`) must wrap the app tree
 * for toasts to actually render. The calls above are no-ops until a provider
 * is mounted.
 */

export type ToastType = "success" | "error" | "info";

export interface ToastOptions {
  /** Main message text shown in the toast body. */
  message: string;
  /** Optional bold title rendered above the message. */
  title?: string;
  /**
   * Auto-dismiss duration in milliseconds.
   * Defaults to 4000 ms. Pass 0 to disable auto-dismiss (requires manual swipe/press).
   */
  duration?: number;
  /** Called when the toast is tapped. */
  onPress?: () => void;
  /** When true, renders the variant icon on the left side of the toast. */
  showLeftIcon?: boolean;
  /** When true, renders a ✕ dismiss button on the right side of the toast. Defaults to false. */
  showCloseIcon?: boolean;
}

export interface ToastItem extends ToastOptions {
  /** Unique ID assigned automatically. */
  id: string;
  type: ToastType;
}

// ─── Singleton handler ───────────────────────────────────────────────────────
// The ToastProvider registers its dispatch function here on mount and clears it
// on unmount. This lets `toast.*` calls be made from anywhere (outside React
// tree) without prop drilling or needing a useToast() hook.

let _handler: ((item: ToastItem) => void) | null = null;
let _idSeed = 0;

function nextId(): string {
  return `toast-${Date.now()}-${(_idSeed += 1)}`;
}

/** Called internally by ToastProvider — do not call directly in product code. */
export function _registerToastHandler(fn: (item: ToastItem) => void): void {
  _handler = fn;
}

/** Called internally by ToastProvider on unmount. */
export function _unregisterToastHandler(): void {
  _handler = null;
}

// ─── Public API ──────────────────────────────────────────────────────────────

function show(type: ToastType, opts: ToastOptions): void {
  _handler?.({ ...opts, id: nextId(), type });
}

export const toast = {
  /** Display a ✓ green success toast. */
  success: (opts: ToastOptions) => show("success", opts),
  /** Display a ✗ red error toast. */
  error: (opts: ToastOptions) => show("error", opts),
  /** Display an ℹ blue informational toast. */
  info: (opts: ToastOptions) => show("info", opts),
} as const;
